import i18n from '../../i18n';

export type AttachmentKind = 'image' | 'audio' | 'video' | 'document';

export interface PendingAttachment {
  id: string;
  file: File;
  kind: AttachmentKind;
  previewUrl: string | null;
}

export const ATTACHMENT_ACCEPT = 'image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.rtf,.zip';
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']);
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'avi', 'webm', 'mkv']);
const DOCUMENT_EXTENSIONS = new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'txt', 'rtf', 'zip']);

const DOCUMENT_MIME_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv',
  'text/plain',
  'application/rtf',
  'text/rtf',
  'application/zip',
  'application/x-zip-compressed',
]);

const getExtension = (fileName: string) => fileName.split('.').pop()?.trim().toLowerCase() ?? '';

const generateAttachmentId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const classifyAttachment = (file: File): AttachmentKind => {
  const mimeType = file.type.toLowerCase();
  const extension = getExtension(file.name);

  if (mimeType.startsWith('image/') || IMAGE_EXTENSIONS.has(extension)) {
    return 'image';
  }

  if (mimeType.startsWith('audio/') || AUDIO_EXTENSIONS.has(extension)) {
    return 'audio';
  }

  if (mimeType.startsWith('video/') || VIDEO_EXTENSIONS.has(extension)) {
    return 'video';
  }

  return 'document';
};

export const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const validateAttachmentFile = (file: File) => {
  if (file.size <= 0) {
    return i18n.t('fileIsEmpty', { ns: 'common' });
  }

  if (file.size > MAX_ATTACHMENT_BYTES) {
    return i18n.t('fileExceedsLimit', {
      ns: 'common',
      limit: formatBytes(MAX_ATTACHMENT_BYTES),
    });
  }

  const mimeType = file.type.toLowerCase();
  const extension = getExtension(file.name);
  const kind = classifyAttachment(file);

  if (kind === 'document' && !DOCUMENT_EXTENSIONS.has(extension) && !DOCUMENT_MIME_TYPES.has(mimeType)) {
    return i18n.t('unsupportedFileType', { ns: 'common' });
  }

  return null;
};

export const createPendingAttachment = (file: File): PendingAttachment => {
  const kind = classifyAttachment(file);
  return {
    id: generateAttachmentId(),
    file,
    kind,
    previewUrl: kind === 'document' ? null : URL.createObjectURL(file),
  };
};

export const selectPendingAttachments = (files: File[]) => {
  const attachments: PendingAttachment[] = [];
  const errors: string[] = [];

  for (const file of files) {
    const validationError = validateAttachmentFile(file);
    if (validationError) {
      errors.push(`${file.name}: ${validationError}`);
      continue;
    }

    attachments.push(createPendingAttachment(file));
  }

  return { attachments, errors };
};

export const revokePendingAttachment = (attachment: PendingAttachment) => {
  if (attachment.previewUrl) {
    URL.revokeObjectURL(attachment.previewUrl);
  }
};

export const revokePendingAttachments = (attachments: PendingAttachment[]) => {
  attachments.forEach(revokePendingAttachment);
};
